export interface SectionConfig {
  id: string;           // '3.1' … '3.10'
  title: string;
  questionText: string | null;
  minChars: number | null;
  maxChars: number | null;
  maxScore: number;
  mandatoryForNew: boolean;
  mandatoryForRenewal: boolean;
  displayOrder: number;
}

export interface ListSectionsResponse {
  sections: SectionConfig[];
}

export interface SectionConfigAdmin extends SectionConfig {
  /** UUID primary key from tenant_section_config */
  dbId: string;
  /** Program year this config applies to */
  year: number;
  /** e.g. 'sole_trader' | 'company' | null (both) */
  formType: 'sole_trader' | 'company' | null;
  /** 'standard' = 4 rubric sub-criteria; 'simple' = single 0–max score */
  rubricType: 'standard' | 'simple';
  /** Full question text shown to the applicant */
  questionText: string | null;
  /** Hint text for suggested supporting attachments */
  suggestedAttachments: string | null;
}

export interface SectionConfigUpdateRequest {
  title?: string;
  minChars?: number | null;
  maxChars?: number | null;
  maxScore?: number;
  mandatoryForNew?: boolean;
  mandatoryForRenewal?: boolean;
  displayOrder?: number;
  formType?: 'sole_trader' | 'company' | null;
  rubricType?: 'standard' | 'simple';
  questionText?: string | null;
  suggestedAttachments?: string | null;
}

export interface SectionConfigCreateRequest {
  sectionId: string;
  year?: number;
  title: string;
  minChars?: number | null;
  maxChars?: number | null;
  maxScore?: number;
  mandatoryForNew?: boolean;
  mandatoryForRenewal?: boolean;
  formType?: 'sole_trader' | 'company' | null;
  rubricType?: 'standard' | 'simple';
  questionText?: string | null;
  suggestedAttachments?: string | null;
}

// ---------------------------------------------------------------------------
// Scoring rules — per-tenant, per-year
// ---------------------------------------------------------------------------

export interface ScoringRules {
  id: string;
  tenantId: string;
  year: number;
  /** Fraction of max_base_score required for new applicants to pass (e.g. 0.6) */
  passThresholdNew: number;
  /** Fraction of max_base_score required for renewal applicants to pass */
  passThresholdRenewal: number;
  /** Total available base points (excluding bonus) */
  maxBaseScore: number;
  /** Maximum bonus points available */
  maxBonusScore: number;
  /** Fraction of max_base_score required for "Kiváló pályázó" suggestion (e.g. 0.9) */
  excellentThreshold: number;
  /** Minimum bonus points required for "Kiváló pályázó" suggestion */
  excellentBonusMin: number;
}

export interface ScoringRulesUpdateRequest {
  year: number;
  passThresholdNew?: number;
  passThresholdRenewal?: number;
  maxBaseScore?: number;
  maxBonusScore?: number;
  excellentThreshold?: number;
  excellentBonusMin?: number;
}

export interface DeclarationConfig {
  id: string;
  declarationKey: string;
  groupType: 'exclusion' | 'submission';
  label: string;
  isExclusionFactor: boolean;
  // When true, answering TRUE (igen) triggers the exclusion warning.
  // When false, the statement is positive and answering FALSE (nem) triggers it.
  exclusionOnTrue: boolean;
  displayOrder: number;
  isActive: boolean;
}

export interface DeclarationConfigUpdateRequest {
  label?: string;
  isExclusionFactor?: boolean;
  displayOrder?: number;
  isActive?: boolean;
}

export interface DeclarationConfigCreateRequest {
  declarationKey: string;
  groupType: 'exclusion' | 'submission';
  label: string;
  isExclusionFactor?: boolean;
  displayOrder?: number;
}

export interface ListDeclarationsResponse {
  exclusion: DeclarationConfig[];
  submission: DeclarationConfig[];
}

// ---------------------------------------------------------------------------
// Chamber Registry
// ---------------------------------------------------------------------------

export interface ChamberRegistryEntry {
  id:              string;
  taxId:           string;
  email:           string;
  email2:          string | null;
  isRenewal:       boolean;
  companyName:     string | null;
  hqZip:           string | null;
  hqCity:          string | null;
  hqStreet:        string | null;
  memberSince:     string | null;
  chapter:         string | null;
  region:          string | null;
  importedAt:      string;
  importedByEmail: string | null;
}

export interface CheckRegistryResponse {
  ok:          true;
  email2:      string | null;
  isRenewal:   boolean;
  companyName: string | null;
  hqZip:       string | null;
  hqCity:      string | null;
  hqStreet:    string | null;
  memberSince: string | null;
  chapter:     string | null;
  region:      string | null;
}

export interface ChamberRegistryImportResult {
  imported:        number;
  skipped:         number;
  skippedDetails:  string[];
}

// ---------------------------------------------------------------------------
// Invites
// ---------------------------------------------------------------------------

export interface InviteInfo {
  email:       string;
  companyName: string;
  expiresAt:   string;
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

export interface AuditLogEntry {
  id:          string;
  eventType:   string;
  targetEmail: string | null;
  taxId:       string | null;
  ipAddress:   string | null;
  success:     boolean;
  note:        string | null;
  actorEmail:  string | null;
  createdAt:   string;
}

export interface AdminInviteEntry {
  id:             string;
  inviteeEmail:   string;
  status:         'pending' | 'accepted' | 'expired';
  expiresAt:      string;
  createdAt:      string;
  acceptedAt:     string | null;
  companyName:    string;
  invitedByEmail: string | null;
}

export interface StaffInviteInfo {
  email:     string;
  role:      'tenant_admin' | 'reviewer';
  expiresAt: string;
}

export interface StaffInviteEntry {
  id:            string;
  email:         string;
  role:          'tenant_admin' | 'reviewer';
  status:        'pending' | 'accepted' | 'expired';
  expiresAt:     string;
  createdAt:     string;
  acceptedAt:    string | null;
  invitedByEmail: string | null;
}

/** Human-readable Hungarian labels for each user role. */
export const ROLE_LABELS: Record<string, string> = {
  company_user: 'Beküldő',
  reviewer:     'Bíráló',
  tenant_admin: 'Adminisztrátor',
  editor:       'Szerkesztő',
  super_admin:  'Super Admin',
};

// ---------------------------------------------------------------------------
// Help content (Segítség modal)
// ---------------------------------------------------------------------------

export interface HelpContent {
  contactName:     string | null;
  contactEmail:    string | null;
  contactPhone:    string | null;
  contactExtra:    string | null;
  publicationUrl:  string | null;
}

export interface HelpContentUpdateRequest {
  contactName?:    string | null;
  contactEmail?:   string | null;
  contactPhone?:   string | null;
  contactExtra?:   string | null;
  publicationUrl?: string | null;
}

// ---------------------------------------------------------------------------
// Deadlines
// ---------------------------------------------------------------------------

export interface DeadlinesConfig {
  applicationStart: string | null;
  applicationEnd:   string | null;
  reviewStart:      string | null;
  reviewEnd:        string | null;
  /** Earliest allowed chamber membership date (ISO date, e.g. "2024-01-01"). Null = no restriction. */
  minMemberSince:   string | null;
}

export interface DeadlinesUpdateRequest {
  applicationStart?: string | null;
  applicationEnd?:   string | null;
  reviewStart?:      string | null;
  reviewEnd?:        string | null;
  minMemberSince?:   string | null;
}

// ---------------------------------------------------------------------------
// Document library
// ---------------------------------------------------------------------------

export interface DocumentItem {
  id:          string;
  displayName: string;
  fileSize:    number | null;
  mimeType:    string | null;
  createdAt:   string;
  downloadUrl: string | null;
}

export interface DocumentRenameRequest {
  displayName: string;
}
